import { useCallback, useEffect } from 'react'
import {
  getProductTypesList,
  getCountryName,
  buildTimelineEventData,
  createFireflyEvent,
} from '../../../../common/helper'
import {
  RFI_TYPE_LOCATION,
  FIRST_INITIATED,
  FIRST_SUBMITTED,
  FIRST_APPROVED,
  CODE_RFI_TYPE_LOCATION,
  CODE_RFI_TYPE_SUPPLIER,
  TEXT_APPROVE,
} from '../../../../common/commonConstants'
import fireflyData from '../../../../common/fireflyData'
import { isEmpty } from 'lodash'
import { userInfoData } from '../../../Main'

export default function useRFIAnalytics({
  isReadonlyProp,
  sendEvent,
  rfiType,
  d,
  auditData,
}) {
  useEffect(() => {
    if (!isReadonlyProp && userInfoData) {
      sendEvent(
        createFireflyEvent('selected_mfe', 'LCM MFE EXTERNAL', userInfoData),
      )
    }
  }, [isReadonlyProp, sendEvent])

  const pushDataToFirefly = useCallback(
    (userAction, prevUserAction) => {
      const rfiLabel = rfiType === RFI_TYPE_LOCATION ? 'RFI2' : 'RFI1'
      const tag = `${rfiLabel} ${userAction} action for `.concat(rfiType)
      const keyBase = `selected_${rfiLabel.toLowerCase()}_${prevUserAction}_to_${userAction}`

      const updated = d?.rfiObject?.last_vetting_status?.status_updated_on
      const dt = updated ? new Date(updated) : null
      const daysSince =
        dt && !isNaN(dt.getTime())
          ? Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24))
          : undefined

      if (daysSince === undefined) return

      const list = getProductTypesList(rfiType, d?.rfiObject) || []
      list.forEach((productCapability) => {
        // legacy-shaped eventData (kept, and used to build the new sendEvent payload)
        const eventData = {
          user: userInfoData,
          view: {
            globalName: fireflyData.view.globalName,
            name: tag,
            type: fireflyData.view.type.button,
          },
          event: {
            type: fireflyData.event.type.click,
          },
          customInteraction: {
            location: `${rfiLabel} - ${prevUserAction} to ${userAction}`,
            key: d?.rfiObject?.status_version
              ? `${keyBase}_${d?.rfiObject?.status_version}`
              : `${keyBase}_1`,
            value:
              rfiType === RFI_TYPE_LOCATION
                ? `${d?.rfiObject?.supplier_id}-${d?.rfiObject?.location_id}`
                : d?.rfiObject?.supplier_id,
          },
          customMetrics: {
            metric1: getCountryName(d?.rfiObject, rfiLabel),
            metric2: productCapability,
            metric3: d?.rfiObject?.manufacturing_type,
            ...(daysSince !== undefined ? { metric4: daysSince } : {}),
          },
        }

        const payload = {
          event: {
            type: 'CLICK',
            id: eventData.customInteraction.key,
            value: String(daysSince),
          },
          supplier_info: {
            id: d?.rfiObject?.supplier_id,
            location_id: d?.rfiObject?.location_id,
            manufacturing_type: d?.rfiObject?.manufacturing_type,
            action: productCapability,
          },
          client: {
            page_name: eventData.customInteraction.location,
            version: eventData.customMetrics.metric1,
            employee_login_id: userInfoData?.id,
          },
        }

        sendEvent(payload)
      })
    },
    [d?.rfiObject, rfiType, sendEvent],
  )

  const publishRFITimelineData = useCallback(
    (newVettingStatusCode) => {
      let action = ''

      const rfiCode =
        rfiType === RFI_TYPE_LOCATION
          ? CODE_RFI_TYPE_LOCATION
          : CODE_RFI_TYPE_SUPPLIER

      const prevCode = d?.rfiDataFromApi?.vetting_status_code
      if (String(prevCode) === '0') {
        action = FIRST_INITIATED
      } else if (
        String(prevCode) === '1' &&
        String(newVettingStatusCode) === '2'
      ) {
        action = FIRST_SUBMITTED
      } else if (
        String(newVettingStatusCode) === '3' ||
        String(newVettingStatusCode) === '6'
      ) {
        const audits = Array.isArray(auditData) ? auditData : []

        const isFirstApproval = isEmpty(audits)
          ? true
          : audits.filter(
              (audit) =>
                String(audit?.rfi_string || '').toLowerCase() ===
                  String(rfiCode).toLowerCase() &&
                String(audit?.vetting_status || '')
                  .toLowerCase()
                  .includes(String(TEXT_APPROVE).toLowerCase()),
            ).length === 0

        if (isFirstApproval) {
          action = FIRST_APPROVED
        }
      }

      if (!isEmpty(action)) {
        const eventData = buildTimelineEventData(
          action,
          d?.rfiObject,
          userInfoData,
          String(rfiCode).toUpperCase(),
        )
        sendEvent(eventData)
      }
    },
    [auditData, d?.rfiDataFromApi, d?.rfiObject, rfiType, sendEvent],
  )

  return { pushDataToFirefly, publishRFITimelineData }
}
